import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useSignIn } from '@clerk/tanstack-react-start';
import {
  Stack,
  Input,
  Button,
  Text,
  Link,
  Separator,
  Box,
} from '@fragments-sdk/ui';
import { Beams } from '@/components/Beams';
import s from './sign-in.module.scss';

export const Route = createFileRoute('/sign-in')({
  component: SignInPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    cli_port?: number;
    cli_nonce?: string;
    invite?: string;
    next?: string;
  } => ({
    ...(search.cli_port ? { cli_port: Number(search.cli_port) } : {}),
    ...(search.cli_nonce ? { cli_nonce: search.cli_nonce as string } : {}),
    ...(typeof search.invite === 'string' ? { invite: search.invite } : {}),
    ...(typeof search.next === 'string' && search.next.startsWith('/')
      ? { next: search.next }
      : {}),
  }),
});

function SignInPage() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const navigate = useNavigate();
  const { cli_port, cli_nonce, invite, next } = Route.useSearch();

  // CLI auth must always work regardless of the feature flag
  const isCliAuth = Boolean(cli_port && cli_nonce);
  const isInviteAuth = Boolean(invite);

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const postAuthUrl = isCliAuth
    ? `/cli-auth?port=${cli_port}&nonce=${cli_nonce}`
    : invite
      ? `/invites?invite=${encodeURIComponent(invite)}`
      : next
        ? next
        : '/';

  async function handleOAuth(strategy: 'oauth_google' | 'oauth_microsoft') {
    if (!isLoaded) return;
    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: '/sso-callback',
        redirectUrlComplete: postAuthUrl,
      });
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'OAuth sign-in failed.');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;

    setError('');
    setLoading(true);

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate({ to: postAuthUrl });
      }
    } catch (err: any) {
      const message =
        err.errors?.[0]?.longMessage ||
        err.errors?.[0]?.message ||
        'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={s.container}>
      {/* Left branded panel */}
      <div className={s.leftPanel}>
        <Beams className={s.beams} />
        <div className={s.leftContent}>
          <img
            src="/fragments-logo.png"
            alt="Fragments"
            width={48}
            height={48}
          />
          <Text
            size="2xl"
            weight="bold"
            style={{ color: '#fff', lineHeight: 1.3 }}
          >
            Ship AI&#8209;generated UI with confidence.
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
            Monitor design policy compliance, track violations, and enforce
            design system standards across your AI&#8209;generated interfaces.
          </Text>
        </div>
      </div>

      {/* Right form panel */}
      <div className={s.rightPanel}>
        <Box className={s.formWrapper}>
          <Stack gap="lg">
            <div className={s.mobileBranding}>
              <img
                src="/fragments-logo.png"
                alt="Fragments"
                width={36}
                height={36}
              />
            </div>

            <Stack gap="xs">
              <Text as="h1" size="xl" weight="bold">
                {isCliAuth ? 'Sign in to authorize CLI' : 'Sign in'}
              </Text>
              <Text color="secondary">
                {isCliAuth
                  ? 'Sign in to connect your terminal to Fragments Cloud.'
                  : isInviteAuth
                    ? 'Sign in to accept your team invite.'
                    : 'Welcome back! Please enter your details.'}
              </Text>
            </Stack>

            <Stack gap="sm">
              <Button
                variant="outline"
                onClick={() => handleOAuth('oauth_google')}
                disabled={!isLoaded}
                className={s.oauthBtn}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </Button>
              <Button
                variant="outline"
                onClick={() => handleOAuth('oauth_microsoft')}
                disabled={!isLoaded}
                className={s.oauthBtn}
              >
                <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden>
                  <path fill="#f35325" d="M1 1h10v10H1z" />
                  <path fill="#81bc06" d="M12 1h10v10H12z" />
                  <path fill="#05a6f0" d="M1 12h10v10H1z" />
                  <path fill="#ffba08" d="M12 12h10v10H12z" />
                </svg>
                Continue with Microsoft
              </Button>
            </Stack>

            <Separator label="or" spacing="sm" />

            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={setEmail}
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={setPassword}
                  required
                />
                {error && (
                  <Text size="sm" color="primary">
                    {error}
                  </Text>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !isLoaded}
                >
                  {loading ? 'Signing in\u2026' : 'Sign In'}
                </Button>
              </Stack>
            </form>

            <Text size="sm" color="tertiary" style={{ textAlign: 'center' }}>
              Don&apos;t have an account?{' '}
              <Link
                href={
                  invite
                    ? `/sign-up?invite=${encodeURIComponent(invite)}`
                    : '/sign-up'
                }
              >
                Sign up
              </Link>
            </Text>
          </Stack>
        </Box>
      </div>
    </div>
  );
}
